import "pixi";
import "phaser";

const MUSIC_VOLUME = 0.6;
const MUSIC_TRANSITION_TIME = 0.4;

type Sound = "slash" | "hit_ground" | "walking" | "collect_star" | "hurt" | "hurt_echo"
           | "laser" | "firework" | "firework_light" | "button_hover" | "button_click";

class Music {
    element: HTMLAudioElement;
    masterGain: GainNode;
    regGain: GainNode;
    filGain: GainNode;
}

export default class AudioEngine {
    static instance: AudioEngine | null = null;

    game: Phaser.Game;
    sounds: { [key: string]: Phaser.Sound } = {};

    music: Music | null = null;

    static initialize(game: Phaser.Game) {
        if(this.instance === null) {
            this.instance = new AudioEngine(game);
        }
    }

    static get(): AudioEngine {
        if(this.instance === null) {
            throw "AudioEngine must be initialized before it can be used";
        }
        return this.instance;
    }

    private constructor(game: Phaser.Game) {
        this.game = game;

        // Don't stop the sounds when the game pauses (i.e. loses focus)
        this.game.sound.muteOnPause = false;

        let ctx = <AudioContext>this.game.sound.context;

        if(!this.game.sound.usingWebAudio) {
            alert(`WebAudio is not supported, please try in a more recent browser.`);
        } else {
            let audioElement = document.createElement('audio');
            audioElement.crossOrigin = "anonymous";
            audioElement.loop = true;
            audioElement.volume = 0;

            let sourceOgg = document.createElement('source');
            sourceOgg.type = 'audio/ogg';
            sourceOgg.src = require("../assets/music/track4_merged.ogg");
            audioElement.appendChild(sourceOgg);

            let sourceAac = document.createElement('source');
            sourceAac.type = 'audio/aac';
            sourceAac.src = require("../assets/music/track4_merged.aac");
            audioElement.appendChild(sourceAac);

            audioElement.play().then(() => {
                let musicSource = ctx.createMediaElementSource(audioElement);

                let splitter = ctx.createChannelSplitter(4);
                musicSource.connect(splitter);

                let mergeReg = ctx.createChannelMerger(2);
                let mergeFil = ctx.createChannelMerger(2);
                splitter.connect(mergeReg, 0, 0);
                splitter.connect(mergeReg, 1, 1);
                splitter.connect(mergeFil, 2, 0);
                splitter.connect(mergeFil, 3, 1);

                let gainReg = ctx.createGain();
                let gainFil = ctx.createGain();
                mergeReg.connect(gainReg);
                mergeFil.connect(gainFil);

                let gainMaster = ctx.createGain();
                gainReg.connect(gainMaster);
                gainFil.connect(gainMaster);

                gainMaster.connect(ctx.destination);

                gainReg.gain.value = 1;
                gainFil.gain.value = 0;

                if(this.game.sound.mute) {
                    audioElement.volume = 0;
                } else {
                    audioElement.volume = MUSIC_VOLUME;
                }

                this.music = {
                    element: audioElement,
                    masterGain: gainMaster,
                    regGain: gainReg,
                    filGain: gainFil,
                }
            });

        }

        // Load the sound effects
        this.sounds["slash"] = this.game.add.audio("slash");
        this.sounds["slash"].volume = 0.32;
        this.sounds["slash"].loop = false;

        this.sounds["hit_ground"] = this.game.add.audio("hit_ground");
        this.sounds["hit_ground"].volume = 0.2;
        this.sounds["hit_ground"].loop = false;

        this.sounds["walking"] = this.game.add.audio("walk_roof");
        this.sounds["walking"].volume = 0.15;
        this.sounds["walking"].loop = true;

        this.sounds["collect_star"] = this.game.add.audio("collect_star");
        this.sounds["collect_star"].volume = 0.4;
        this.sounds["collect_star"].loop = false;

        this.sounds["hurt"] = this.game.add.audio("hurt");
        this.sounds["hurt"].volume = 0.2;
        this.sounds["hurt"].loop = false;

        this.sounds["hurt_echo"] = this.game.add.audio("hurt_echo");
        this.sounds["hurt_echo"].volume = 0.15;
        this.sounds["hurt_echo"].loop = false;

        this.sounds["laser"] = this.game.add.audio("laser");
        this.sounds["laser"].volume = 0.25;
        this.sounds["laser"].loop = false;

        this.sounds["firework"] = this.game.add.audio("firework");
        this.sounds["firework"].volume = 0.65;
        this.sounds["firework"].loop = false;
        this.sounds["firework"].allowMultiple = true;

        this.sounds["firework_light"] = this.game.add.audio("firework");
        this.sounds["firework_light"].volume = 0.3;
        this.sounds["firework_light"].loop = false;
        this.sounds["firework_light"].allowMultiple = true;

        this.sounds["button_hover"] = this.game.add.audio("button_hover");
        this.sounds["button_hover"].volume = 0.2;
        this.sounds["button_hover"].loop = false;
        this.sounds["button_hover"].allowMultiple = true;

        this.sounds["button_click"] = this.game.add.audio("button_click");
        this.sounds["button_click"].volume = 0.5;
        this.sounds["button_click"].loop = false;
    }

    startMusic(): void {
        // Naw, the music already started in the constructor
    }

    play(name: Sound, restart=true) {
        this.sounds[name].play(undefined, undefined, undefined, undefined, restart);
    }

    stop(name: Sound) {
        this.sounds[name].stop();
    }

    stopAllSounds(exceptions: Sound[] = []) {
        for(let s in this.sounds) {
            if(exceptions.some(e => e === s)) continue;
            this.sounds[s].stop();
        }
    }

    mute(): void {
        this.game.sound.mute = true;
        if(this.music !== null) {
            this.music.element.volume = 0;
        }
    }

    unmute(): void {
        this.game.sound.mute = false;
        if(this.music !== null) {
            this.music.element.volume = MUSIC_VOLUME;
        }
    }

    switchTo(target: "regular" | "filtered") {
        if(this.music === null) return;

        let ctx = <AudioContext>this.game.sound.context;
        let regGain = this.music.regGain;
        let filGain = this.music.filGain;

        if(target == "regular") {
            regGain.gain.cancelScheduledValues(ctx.currentTime);
            regGain.gain.linearRampToValueAtTime(1, ctx.currentTime + MUSIC_TRANSITION_TIME);
            filGain.gain.cancelScheduledValues(ctx.currentTime);
            filGain.gain.linearRampToValueAtTime(0, ctx.currentTime + MUSIC_TRANSITION_TIME);
        } else {
            regGain.gain.cancelScheduledValues(ctx.currentTime);
            regGain.gain.linearRampToValueAtTime(0, ctx.currentTime + MUSIC_TRANSITION_TIME);
            filGain.gain.cancelScheduledValues(ctx.currentTime);
            filGain.gain.linearRampToValueAtTime(1, ctx.currentTime + MUSIC_TRANSITION_TIME);
        }
    }
}



// WEBPACK FOOTER //
// ./src/audio_engine.ts